import { useLocation, useNavigate } from 'react-router-dom';
import { useMemo, useCallback } from 'react';

export const useHashModal = (entityId?: string | number, expectedAction?: string) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse hash with improved logic
  const hash = location.hash;
  
  // Memoize parsing logic
  const { currentId, currentAction } = useMemo(() => {
    let id: string | null = null;
    let action: string | null = null;
    
    // Check if hash starts with #/
    if (hash.startsWith('#/')) {
      const parts = hash.slice(2).split('/'); // Remove #/ and split
      
      if (parts.length === 1) {
        const firstPart = parts[0];
        
        // Check if first part is a known action (non-numeric)
        if (firstPart === 'new' || isNaN(Number(firstPart))) {
          // It's an action without an entity ID
          action = firstPart;
          id = null;
        } else {
          // It's a numeric ID, default action is 'view'
          id = firstPart;
          action = 'view';
        }
      } else if (parts.length === 2) {
        // Format: #/id/action
        id = parts[0];
        action = parts[1];
      }
    }
    return { currentId: id, currentAction: action };
  }, [hash]);
  
  // Determine if modal should be open
  const isOpen = useMemo(() => {
    if (!entityId) return false;
    
    const matchesId = currentId === entityId.toString();
    
    if (expectedAction) {
      return matchesId && currentAction === expectedAction;
    } else {
      return matchesId && (!currentAction || currentAction === 'view');
    }
  }, [entityId, currentId, currentAction, expectedAction]);
  
  const onClose = useCallback(() => {
    const basePath = location.pathname.replace(/\/+$/, '');
    navigate(basePath); // Clear hash
  }, [location.pathname, navigate]);
  
  const navigateToModal = useCallback((targetId: string | number, targetAction?: string) => {
    // Normalize basePath - remove trailing slashes to avoid duplication
    const basePath = location.pathname.replace(/\/+$/, '');
    
    // Handle special cases
    if (!targetId || targetId === '') {
      navigate(basePath); // Clear hash
      return;
    }
    
    // Handle 'new' action specially
    if (targetId === 'new') {
      navigate(`${basePath}/#/new`);
      return;
    }
    
    // Normal ID with optional action - keep the leading slash before and after #
    const hashPath = targetAction ? `/#/${targetId}/${targetAction}` : `/#/${targetId}`;
    const fullUrl = `${basePath}${hashPath}`;
    navigate(fullUrl);
  }, [location.pathname, navigate]);
  
  return useMemo(() => ({
    isOpen,
    onClose,
    navigateToModal,
    currentId,
    currentAction
  }), [isOpen, onClose, navigateToModal, currentId, currentAction]);
}; 